/**
 * Vocabulary shared by the server and both portals of the employee document module.
 */
export const EDP_PACKAGE_STATUSES = [
  'invited',
  'in_progress',
  'submitted',
  'corrections_requested',
  'released',
  'usps_returned',
  'cancelled',
] as const;
export type EdpPackageStatus = (typeof EDP_PACKAGE_STATUSES)[number];

export const EDP_PACKAGE_STATUS_LABELS: Record<EdpPackageStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  submitted: 'Awaiting QA review',
  corrections_requested: 'Corrections requested',
  released: 'Released to USPS',
  usps_returned: 'Returned by USPS',
  cancelled: 'Cancelled',
};

/** Who must act next for a package in the given status. */
export const EDP_NEXT_ACTION: Record<EdpPackageStatus, string> = {
  invited: 'Employee',
  in_progress: 'Employee',
  submitted: 'VGS reviewer',
  corrections_requested: 'Employee',
  released: 'USPS recipient',
  usps_returned: 'VGS reviewer',
  cancelled: '—',
};

export const EDP_ITEM_STATUSES = ['pending', 'completed', 'accepted', 'rejected'] as const;
export type EdpItemStatus = (typeof EDP_ITEM_STATUSES)[number];

/**
 * How a form that requires a signature is signed: `digital` is click-to-sign in
 * the browser; `ink` means the employee prints the completed form, signs it by
 * hand, and uploads the scan, which then stands in for the rendered PDF.
 */
export const EDP_SIGNATURE_MODES = ['digital', 'ink'] as const;
export type EdpSignatureMode = (typeof EDP_SIGNATURE_MODES)[number];

export const EDP_ITEM_STATUS_LABELS: Record<EdpItemStatus, string> = {
  pending: 'Not started',
  completed: 'Submitted',
  accepted: 'Accepted',
  rejected: 'Needs correction',
};

/** Standardised rejection reasons; the reviewer adds a free-text comment alongside. */
export const EDP_REJECTION_REASONS = [
  { code: 'illegible', label: 'Image is blurry or unreadable' },
  { code: 'incomplete', label: 'Form is missing information' },
  { code: 'wrong_document', label: 'Wrong document uploaded' },
  { code: 'expired', label: 'Document is expired' },
  { code: 'mismatch', label: 'Name or details do not match' },
  { code: 'cut_off', label: 'Part of the document is cut off' },
  { code: 'other', label: 'Other (see comment)' },
] as const;
export type EdpRejectionReason = (typeof EDP_REJECTION_REASONS)[number]['code'];

/** Employee uploads (credentials and signed scans): PDF only; photographs are refused. */
export const EDP_ALLOWED_MIME_TYPES: Record<string, readonly string[]> = {
  'application/pdf': ['.pdf'],
};
